import {
  STATE_BOOT,
  STATE_LOAD,
  STATE_WELCOME,
  STATE_GAME,
  STATE_GAME_INTRO,
  STATE_GAME_COMPLETE,
  STATE_CHARACTER_INTRO,
  STATE_LEVELEND,
  STATE_LEVELLOSE,
  TIMER_SHADOW_COLOUR,
  STATE_CUTSCENE,
} from '../constants/AppConstants';

import UIModel from 'app/model/UIModel';
import Instructions from 'app/ui/instructions/Instructions';
import GameModel from 'app/model/GameModel';
import Hud from 'app/ui/Hud';
import Credits from 'app/ui/credits/Credits';
import ConfigModel from 'app/model/ConfigModel';
import { STATE_GAMEEND, STATE_GAMEOVER } from 'app/constants/AppConstants';
import StateController from 'app/controller/StateController';
// import CheatController from 'app/controller/CheatController';
// import ShortcutController from 'app/controller/ShortcutController';
import LevelModel from 'app/model/LevelModel';
import { zeroPad } from 'utils/utils';
import * as AppTrackingEvents from 'app/constants/AppTrackingEvents';

export default class GameController {
  constructor({ game, settings, hud, deviceController }) {
    this.game = game;
    this.settings = settings;
    this.hud = hud;
    this.deviceController = deviceController;
  }

  init({ config }) {
    /**
     * Models
     */
    this.config = new ConfigModel({ game: this.game, config });

    if (!this.config.show_rotate_screen) {
      this.game.aspectController.disableRotateScreen();
    }

    this._gameModel = new GameModel({ settings: this.settings, gameData: this.config.game });

    this.stateController = new StateController({ game: this.game, transitionType: this.deviceController.getAppTransitionType() });
    // this.cheatController = new CheatController(this.game);
    // this.shortcutController = new ShortcutController(this.game);
    this.game.sounds.addSounds(this.config.audio);
    if (this.game.device.ie || this.game.deviceController.isOldAppleDevice()) {
      console.log('SEPERATE SFX LOADED');
      this.game.sounds.addSounds(this.config.audioIE);
    } else {
      this.game.sounds.addAudioSprites(this.config.audioSprites);
    }

    this.uiModel = new UIModel({ settings: this.settings });

    this.hud.init(this.config.screenhud);

    this.gamePaused = false;
  }

  /*
  ====================================================================================
    STATES
  ====================================================================================
  */
  startBoot() {
    this.game.state.start(STATE_BOOT, true, false);
  }

  startLoad() {
    this.game.state.start(STATE_LOAD, true, false);
  }

  startCharacterSelect() {
    // this.game.sounds.dimBackgroundMusic(false);
    this.game.track(AppTrackingEvents.VIEW_SCREEN_CHARACTER_SELECT);
    this.game.state.start(STATE_CHARACTER_INTRO, true, false);
  }

  startCutscene() {
    this.game.track(AppTrackingEvents.VIEW_SCREEN_CUTSCENE);
    this.game.state.start(STATE_CUTSCENE, true, false);
  }

  showWelcomeScreen() {
    this.game.sounds.dimBackgroundMusic(false);
    this.game.track(AppTrackingEvents.VIEW_SCREEN_WELCOME);
    this.stateController.startState({
      nextState: STATE_WELCOME,
      nextStateParams: [true, false],
    });
  }

  showInstructions() {
    const { instructionsClose } = this.config.buttons;
    this.instructions = new Instructions({
      game: this.game,
      closeBtnData: instructionsClose,
    });
    this.gameModel.setInstructionsViewed(); // don't force them if they have viewed them
    this.instructions.activate();
  }

  closeInstructions() {
    this.instructions.destroy();
  }

  showGameIntro() {
    this.hud.configure({ id: Hud.EMPTY });
    this.stateController.startState({
      nextState: STATE_GAME_INTRO,
      nextStateParams: [true, false],
    });
  }

  setTutorialIsComplete() {
    this.gameModel.setTutorialIsComplete();
  }

  showCredits() {
    const { creditsBack } = this.config.buttons;
    this.credits = new Credits({
      game: this.game,
      closeBtnData: creditsBack,
    });
    this.credits.activate();
  }

  closeCredits() {
    this.hud.configure({ id: Hud.PAUSE, isModal: true });
    this.credits.destroy();
  }

  /*
  ====================================================================================
    END GAME LEVEL
  ====================================================================================
  */

  /*
  ====================================================================================
    PLAYER LEVEL CONTROL
  ====================================================================================
  */

  startNewGame() {
    this.gameModel.reset();
    this.startLevel();
  }

  startLevel(playerLevel) {
    this.game.sounds.dimBackgroundMusic(true);

    if (playerLevel) {
      this.gameModel.level = playerLevel;
    }

    const levelData = this.config.levels[this.gameModel.level - 1];

    this.game.track(AppTrackingEvents.getLevelString(this.gameModel.level));

    this.stateController.startState({
      nextState: STATE_GAME,
      nextStateParams: [true, false, { levelData, playerLevel: this.gameModel.level, showTutorial: this.gameModel.getIsTutorialMode() }],
    });
    this.hud.configure({ id: Hud.PLAYING, isVisible: false });
  }

  replayLevel() {
    this.stateController.startState({
      nextState: STATE_GAME,
      nextStateParams: [true, false],
    });
  }

  nextLevel() {
    if (this.gameModel.level === this.config.levels.length) {
      this.showGameComplete();
    } else {
      this.gameModel.levelUp();
      this.playLevel();
    }
  }

  /*
  ====================================================================================
    GAME PLAY
  ====================================================================================
  */

  showGameEnd({ win }) {
    this.stateController.startState({
      nextState: STATE_GAMEEND,
      nextStateParams: [true, false, { win, character: this.gameModel.selectedCharacter }],
    });
  }

  showLevelEnd({ win }) {
    // if (this.hud.hudGamePause) this.hud.closeGamePause();

    if (win) {
      if (this.gameModel.getCurrentLevel() === this.configModel.getLevelCount()) {
        this.game.track(AppTrackingEvents.VIEW_SCREEN_GAME_COMPLETE);
        this.stateController.startState({
          nextState: STATE_GAMEEND,
          nextStateParams: [true, false, { win, character: this.gameModel.selectedCharacter }],
        });
      } else {
        this.game.track(AppTrackingEvents.getLevelCompleteString(this.gameModel.getCurrentLevel()));
        this.game.track(AppTrackingEvents.VIEW_SCREEN_LEVEL_WIN);
        this.stateController.startState({
          nextState: STATE_LEVELEND,
          nextStateParams: [true, false, { win, character: this.gameModel.selectedCharacter }],
        });
      }
    } else {
      this.game.track(AppTrackingEvents.getLevelLoseString(this.gameModel.getCurrentLevel()));
      this.game.track(AppTrackingEvents.VIEW_SCREEN_LEVEL_LOSE);
      this.stateController.startState({
        nextState: STATE_LEVELLOSE,
        nextStateParams: [true, false, { character: this.gameModel.selectedCharacter }],
      });
    }
  }

  // is time being used here?
  showGameOver() {
    // this.game.sounds.dimBackgroundMusic(false);
    this.stateController.startState({
      nextState: STATE_GAMEEND,
      nextStateParams: [true, false],
    });
    this.hud.configure({ id: Hud.GAMEEND, isVisible: false });
  }

  quitGame() {
    this.hud.deactivate();
    this.gameModel.reset();
    this.showWelcomeScreen();
  }

  restartGame() {
    this.gameModel.reset();
    this.startLevel();
  }

  /*
  ====================================================================================
    PAUSE / RESUME
  ====================================================================================
  */

  pauseGame() {
    if (!this.gamePaused) {
      this.gamePaused = true;
      if (this.game.state.current === STATE_GAME) {
        this.hud.disable();
        this.showPauseScreenHud();
      }
      // setTimeout(this.showPauseScreenHud.bind(this), 0);
    }
  }

  showPauseScreenHud() {
    this.uiModel.setPaused(true);
    this.hud.enable();
    this.hud.activate({ id: Hud.PAUSE, isModal: true });
    this.game.track(AppTrackingEvents.VIEW_SCREEN_PAUSE);
    this.hud.showGamePause();
  }

  resumeGame() {
    this.gamePaused = false;
    if (this.game.state.current === STATE_GAME) {
      this.hud.configure({ id: Hud.PLAYING });
      this.hud.closeGamePause();
      this.uiModel.setPaused(false);
    }
  }

  restartLevel() {
    console.log(this);
    this.resumeGame();
    this.startLevel();
  }

  showGameCredits() {
    this.hud.closeGamePause();
    this.hud.activate({ id: Hud.PAUSE, isModal: true });
    this.hud.showGameCredits();
  }

  closeGameCredits() {
    this.hud.closeGameCredits();
    this.hud.activate({ id: Hud.PAUSE, isModal: true });
    this.hud.showGamePause();
  }

  /*
  ====================================================================================
    GETTERS/SETTERS
  ====================================================================================
  */
  get configModel() {
    return this.config;
  }

  get gameModel() {
    return this._gameModel;
  }
}
